import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";

import { DISCOVERY_CARD_SX } from "/imports/ui/components/discovery";
import { formatPrice } from "/imports/ui/views/ClassesView/classHelpers";

// The row body opens the session page (details + who's playing); Join keeps the
// one-tap path straight to checkout, on the same terms as a class card.
function EventRow({ event, currency, onOpen, onJoin }) {
  const date = DateTime.fromISO(event.date);
  const isFull = event.spotsAvailable === 0;
  const { eligible, reason, needsAssessment } = event.eligibility || {};
  const canJoin = !isFull && eligible !== false && !event.isJoined;

  const status = event.isJoined ? "You're in" : isFull ? "Full" : `${event.spotsAvailable} spot${event.spotsAvailable !== 1 ? "s" : ""} left`;
  const statusColor = event.isJoined ? "success.main" : isFull ? "error.main" : "text.secondary";
  const joinLabel = event.isJoined ? "Joined" : isFull ? "Full" : needsAssessment ? "Assess" : event.wasJoined ? "Re-join" : "Join";
  const timeLine = [event.time, event.duration ? `${event.duration} min` : null, event.trainer ? `with ${event.trainer.name}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      onClick={onOpen}
      sx={{
        ...DISCOVERY_CARD_SX,
        display: "flex",
        alignItems: "stretch",
        overflow: "hidden",
        cursor: "pointer",
        opacity: isFull && !event.isJoined ? 0.65 : 1,
        transition: "border-color 0.15s ease",
        "&:hover": { borderColor: "primary.main" }
      }}
    >
      <Box
        sx={{
          width: { xs: "4rem", md: "4.75rem" },
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 1.5,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          borderRight: "1px solid",
          borderColor: "divider"
        }}
      >
        <Typography color="primary.main" fontWeight={700} sx={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {date.toFormat("ccc")}
        </Typography>
        <Typography fontWeight={800} lineHeight={1.1} sx={{ fontSize: { xs: "1.4rem", md: "1.6rem" } }}>
          {date.toFormat("d")}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: "0.7rem" }}>
          {date.toFormat("LLL")}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1.5, md: 2 } }}>
        <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}>
          {event.name}
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: { xs: "0.78rem", md: "0.875rem" } }}>
          {timeLine}
        </Typography>
        {(event.facility || event.location) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, minWidth: 0 }}>
            <PlaceOutlinedIcon sx={{ fontSize: { xs: "0.95rem", md: "1.05rem" }, color: "primary.main", flexShrink: 0 }} />
            <Typography noWrap color="text.secondary" sx={{ fontSize: { xs: "0.72rem", md: "0.8125rem" } }}>
              {event.facility?.name}
              {event.facility && event.location ? " · " : ""}
              {event.location?.name ? (
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: { xs: "0.875rem", md: "0.9375rem" } }}>
                  {event.location.name}
                </Box>
              ) : null}
            </Typography>
          </Box>
        )}
        {event.levelRequiredNames ? (
          <Chip
            label={event.levelRequiredNames}
            size="small"
            variant="outlined"
            color="warning"
            sx={{ mt: 0.5, maxWidth: "100%", fontSize: { xs: "0.6rem", md: "0.65rem" }, height: { xs: "1.125rem", md: "1.25rem" } }}
          />
        ) : null}
        {!canJoin && !event.isJoined && reason ? (
          <Typography color="warning.main" sx={{ display: "block", mt: 0.5, fontSize: { xs: "0.68rem", md: "0.75rem" } }}>
            {reason}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "center",
          gap: { xs: 0.5, md: 0.75 },
          p: { xs: 1.5, md: 2 },
          flexShrink: 0
        }}
      >
        {event.price != null && (
          <Typography fontWeight={700} sx={{ fontSize: { xs: "0.85rem", md: "0.95rem" }, whiteSpace: "nowrap" }}>
            {formatPrice(event.price, currency)}
          </Typography>
        )}
        <Typography color={statusColor} sx={{ fontSize: { xs: "0.65rem", md: "0.75rem" }, whiteSpace: "nowrap" }}>
          {status}
        </Typography>
        <Button
          size="small"
          variant={canJoin ? "contained" : "outlined"}
          disabled={!canJoin}
          onClick={(e) => {
            e.stopPropagation();
            onJoin();
          }}
          sx={{
            minWidth: { xs: "3.5rem", md: "4rem" },
            px: { xs: 1.25, md: 2 },
            py: { xs: 0.4, md: 0.5 },
            fontSize: { xs: "0.75rem", md: "0.8125rem" },
            ...(canJoin && { color: "common.white" }),
            ...(event.isJoined && { borderColor: "success.main", color: "success.main" })
          }}
        >
          {joinLabel}
        </Button>
      </Box>
    </Box>
  );
}

export default EventRow;
